import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const FULLSCREEN_OVERLAY_Z_INDEX = 10000;

export const getFullscreenContainer = (): HTMLElement | undefined =>
	document.fullscreenElement instanceof HTMLElement ? document.fullscreenElement : undefined;

export const useFullscreenContainer = (active = true): HTMLElement | undefined => {
	const [container, setContainer] = useState<HTMLElement | undefined>(() =>
		active ? getFullscreenContainer() : undefined
	);

	useEffect(() => {
		if (!active) return;

		const syncContainer = () => {
			setContainer(getFullscreenContainer());
		};

		syncContainer();
		document.addEventListener('fullscreenchange', syncContainer);
		return () => document.removeEventListener('fullscreenchange', syncContainer);
	}, [active]);

	return container;
};

export const getFullscreenPopperSlotProps = (container?: HTMLElement) =>
	container
		? { popper: { container, sx: { zIndex: FULLSCREEN_OVERLAY_Z_INDEX } } }
		: { popper: { sx: { zIndex: FULLSCREEN_OVERLAY_Z_INDEX } } };

interface FullscreenPortalProps {
	children: ReactNode;
}

export function FullscreenPortal({ children }: FullscreenPortalProps): JSX.Element {
	const container = useFullscreenContainer();

	if (container) {
		return createPortal(children, container);
	}

	return <>{children}</>;
}
